import { formatParisDateTime, type BundleInput } from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState, type JSX } from 'react';
import { Page, PageHeader } from '@/components/layout/page.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { createBundle, deleteBundle, fetchBundleDetail, updateBundle } from './api.js';
import { BUNDLES_LIST_SEARCH } from './filters.js';
import { BundleForm } from './bundle-form.js';

/**
 * Fiche d'un bundle (`02` §8) — une page dédiée (`/bundles/$sku`,
 * `/bundles/nouveau`), plus un dialogue.
 *
 * En-tête : SKU, statut, « Dernière commande le … » — la question que
 * l'utilisateur se pose devant une liste qui grossit : est-ce que ce bundle
 * sert encore ? Puis le formulaire, puis « Tester ».
 */
export interface BundlePageProps {
  /** `true` sur `/bundles/nouveau` : formulaire vide, pas de section « Tester ». */
  isNew?: boolean;
}

export function BundlePage({ isNew = false }: BundlePageProps): JSX.Element {
  const params: { sku?: string } = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sku = isNew ? '' : (params.sku ?? '');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const detailQuery = useQuery({
    queryKey: queryKeys.bundles.detail(sku),
    queryFn: () => fetchBundleDetail(sku),
    enabled: !isNew && sku !== '',
  });

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: queryKeys.bundles.all });
  }

  const saveMutation = useMutation({
    mutationFn: (input: BundleInput) => (isNew ? createBundle(input) : updateBundle(sku, input)),
    onSuccess: (result) => {
      setErrorMessage(undefined);
      toast.success(isNew ? `Bundle « ${result.bundle.sku} » créé.` : 'Bundle enregistré.');
      for (const warning of result.warnings) {
        toast.error(warning);
      }
      queryClient.setQueryData(queryKeys.bundles.detail(result.bundle.sku), result.bundle);
      invalidate();
      if (isNew) {
        void navigate({ to: '/bundles/$sku', params: { sku: result.bundle.sku } });
      }
    },
    onError: (error: unknown) => {
      setErrorMessage(
        error instanceof ApiError ? error.message : 'Une erreur technique est survenue.',
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteBundle(sku),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Bundle supprimé.');
      invalidate();
      void navigate({ to: '/bundles', search: BUNDLES_LIST_SEARCH });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  if (!isNew && detailQuery.isPending) {
    return (
      <Page width="form">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>
        <Skeleton className="h-44 w-full rounded-lg" />
        <Skeleton className="h-56 w-full rounded-lg" />
      </Page>
    );
  }

  if (!isNew && detailQuery.isError) {
    return (
      <Page width="form">
        <ErrorState
          message={
            detailQuery.error instanceof ApiError
              ? detailQuery.error.message
              : 'Impossible de charger ce bundle.'
          }
          detail={detailQuery.error instanceof ApiError ? detailQuery.error.detail : undefined}
          onRetry={() => {
            void detailQuery.refetch();
          }}
        />
      </Page>
    );
  }

  const detail = isNew ? null : (detailQuery.data ?? null);

  return (
    <Page width="form">
      <PageHeader
        backLink={{ label: 'Retour aux bundles', to: '/bundles', search: BUNDLES_LIST_SEARCH }}
        title={detail ? (detail.label ?? detail.sku) : 'Nouveau bundle'}
        status={
          detail ? (
            <Badge tone={detail.enabled ? 'green' : 'gray'}>
              {detail.enabled ? 'Actif' : 'Inactif'}
            </Badge>
          ) : null
        }
        description={
          detail ? (
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {detail.label === null ? null : (
                <>
                  <span>SKU {detail.sku}</span>
                  <span aria-hidden="true">·</span>
                </>
              )}
              <span className="tabular-nums">
                {detail.lastUsedAt
                  ? `Dernière commande le ${formatParisDateTime(new Date(detail.lastUsedAt))}`
                  : 'Jamais commandé'}
              </span>
            </span>
          ) : (
            'Un bundle est un SKU vendu sur Shopify qui se décompose en plusieurs produits chez Martelet.'
          )
        }
        actions={
          detail ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteOpen(true);
              }}
            >
              Supprimer
            </Button>
          ) : null
        }
      />

      <BundleForm
        key={detail?.sku ?? '__new__'}
        initial={detail}
        isSaving={saveMutation.isPending}
        errorMessage={errorMessage}
        onSubmit={(input) => {
          saveMutation.mutate(input);
        }}
        onCancel={() => {
          void navigate({ to: '/bundles', search: BUNDLES_LIST_SEARCH });
        }}
      />


      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Supprimer ce bundle"
        description={
          detail
            ? `Le bundle « ${detail.sku} » sera supprimé définitivement. Les commandes futures qui contiennent ce SKU l’enverront tel quel chez Martelet.`
            : ''
        }
        confirmLabel="Supprimer"
        isConfirming={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate();
        }}
      />
    </Page>
  );
}
